// People system — docs/ARCHITECTURE_M0.md §5.3.
// Runs after commands/financing. Promotes ramped engineers, applies attrition,
// then publishes this tick's engineer-month budget. Pending hires land in
// closeTick (they arrive next tick); ramping decrement also happens there.

import { DISCIPLINES } from "../types.js";
import type { TickContext } from "../ctx.js";
import { attritionMult, moraleProdMult, starHeads } from "./talent.js";
import { toolProdMult } from "./tools.js";

export function processPeople(ctx: TickContext): void {
  const { s, rng, content } = ctx;
  const eco = content.economy;

  // attrition responds to the labor market and the mood in the building (M3 §9)
  const attrition = eco.attritionMonthly * attritionMult(ctx);

  for (const d of DISCIPLINES) {
    const pool = s.people.pools[d];
    // promote fully ramped
    const done = pool.ramping.filter((r) => r.monthsLeft <= 0);
    pool.ramping = pool.ramping.filter((r) => r.monthsLeft > 0);
    for (const r of done) pool.productive += r.count;
    // attrition (productive pool only in M0), fixed draw order across disciplines
    const expected = pool.productive * attrition * ctx.monthsPerTick;
    if (expected > 0) {
      const losses = Math.min(pool.productive, rng.poisson(expected));
      pool.productive -= losses;
    }
  }

  // roadshow tax (M2, design §3 Stage 3): management is on planes selling the
  // deal, and the whole org slips — the classic file-then-slip trade
  const roadshowMult = s.ipo.process?.phase === "roadshow" ? 1 - eco.ipo.roadshowProductivityHit : 1;
  // morale is a mild throughput factor either way (M3 §9)
  const moraleMult = moraleProdMult(ctx);

  for (const d of DISCIPLINES) {
    const pool = s.people.pools[d];
    const rampingHeads = pool.ramping.reduce((a, r) => a + r.count, 0);
    // stars are extra effective heads; a working in-house tool compounds a little
    ctx.emBudget[d] =
      (pool.productive + eco.rampEffectiveness * rampingHeads + starHeads(ctx, d)) *
      ctx.monthsPerTick *
      roadshowMult *
      moraleMult *
      toolProdMult(ctx, d);
    ctx.allocation[d].budget = ctx.emBudget[d];
  }
}

/** End-of-tick bookkeeping: age ramping entries, land pending hires for next tick. */
export function agePeople(ctx: TickContext): void {
  const { s, content } = ctx;
  for (const d of DISCIPLINES) {
    for (const r of s.people.pools[d].ramping) r.monthsLeft -= ctx.monthsPerTick;
  }
  for (const h of s.people.pendingHires) {
    s.people.pools[h.discipline].ramping.push({ count: h.count, monthsLeft: content.economy.hireRampMonths });
  }
  s.people.pendingHires = [];
}

export function totalHeadcount(ctx: TickContext): number {
  let total = 0;
  for (const d of DISCIPLINES) {
    const pool = ctx.s.people.pools[d];
    total += pool.productive + pool.ramping.reduce((a, r) => a + r.count, 0);
  }
  return total;
}
